// CRM 2.0 Stage 1 card — Application for Booking tabs (M1-F10, rows M1.26 / M1.41).
// Drawn as prototype card2 (and claimCard for Unclaimed): chips (Applied first, SDR,
// Email, Application for Booking not confirmed, KYC, OCR %, AV), days ring, name and
// lead id, lead chip, SM / CRM, unit type, Applied On, Rejection Reason, tracker
// (hidden in Tag Pending), the bottom message and the ⋮ menu (M1-F11; items from Crm2Page).
import React from "react";
import css from "../ui/Crm2Cards.module.css";
import CardMessage from "../ui/CardMessage";
import CardMenu from "../ui/CardMenu";
import { afbConfirmed, avChanged, Bars, Chip, DayRing, fmt, kycDone, leadLabel, lid, ocrText, sdrDone, stepDone, unitLine } from "../ui/cardParts";
import { ApprovalBoxes, nextButton, parkingText } from "./M5Footer";

const num = (v) => Number(v) || 0;

function OcrChip({ c }) {
  const t = ocrText(c);
  return <Chip text={t} tone={t === "OCR < 10%" ? "c-err" : "c-ok"} />;
}

// M4-F7 (DC-34 / DC-153, prototype discBox): discount chips only, from the server (GET /crm2/list › discounts)
function DiscBox({ c }) {
  if (!kycDone(c)) return null; // discounts are checked only after the agreement KYC is filled
  if (c.discounts?.chips?.length) return c.discounts.chips.map(([text, tone]) => <Chip key={text} text={text} tone={tone} dot={false} />);
  if (c.isDiscountApplied && /female/i.test(c.typeOfDiscount || "")) return <Chip text="Female 1% Applied" tone="c-dapp" dot={false} />;
  return null;
}

function tracker(c) {
  const afb = afbConfirmed(c);
  return [
    ["AfB", afb ? 2 : 0],
    [afb ? "KYC" : "KYC locked", afb && kycDone(c) ? 2 : 0],
    ["Draft OTP", stepDone(c, 1, "REVIEW_DRAFT") ? 2 : 0],
    ["AV & plan", c.isApproved ? 2 : 0],
    ["Parking", c.isParkingSelectionSubmitted ? 2 : 0],
    ["SDR", sdrDone(c) ? 2 : 0],
  ];
}

function hoursAgo(ms) {
  const h = Math.max(0, Math.floor((Date.now() - num(ms)) / 3600000));
  return h < 24 ? `${h} hour${h === 1 ? "" : "s"}` : `${Math.floor(h / 24)} day${Math.floor(h / 24) === 1 ? "" : "s"}`;
}

function ClaimCard({ c, onClaim }) {
  return (
    <div className={css.card}>
      <div className={css.cardH}>
        <div className={css.col}>
          <p className={css.u}>{unitLine(c)}</p>
          <div className={css.row}>
            <Chip text={`Unassigned · ${hoursAgo(c.crmCreatedAt)}`} tone="c-err" />
          </div>
        </div>
        <DayRing ring={null} />
      </div>
      <div className={css.col} style={{ gap: ".6rem" }}>
        <div className={css.row} style={{ alignItems: "baseline", gap: ".5rem" }}>
          <p className={css.u}>{c.leadName}</p>
          <p className={css.lid}>{lid(c.leadDisplayId)}</p>
        </div>
        <div className={css.row} style={{ justifyContent: "space-between" }}>
          <Chip text={leadLabel(c)} tone="c-lt" dot={false} />
          {c.isSanctioned === false && <Chip text="Waiting for Sanction" tone="c-warn" dot={false} />}
        </div>
      </div>
      <div className={css.meta}>
        <div className={css.r}>
          <span>VJ SM - {c.smName || "—"}</span>
          <span>VJ CRM - none</span>
        </div>
        <div className={css.r}>
          <span>Why here: no active CRM is tagged to {c.projectName}</span>
        </div>
        <div className={css.r}>
          <span>Applied On: {fmt(c.stage1Day0At || c.crmCreatedAt)}</span>
        </div>
      </div>
      <div className={css.cardF}>
        <p className={css.note}>The first CRM to claim gets it. The email goes when claimed.</p>
        {onClaim && (
          <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onClaim(c)}>Claim</button>
        )}
      </div>
    </div>
  );
}

// onTag(card, "accept" | "reject"): Tag Pending buttons (M2-F6); only passed when the user may decide.
// onClaim(card): Unclaimed card Claim button (M2-F7); only passed when the user may claim.
// onAct(card, what, extra) + can: M5 buttons (Preview and Approve, Select Parking, Approval Pending boxes).
export default function Crm2BookingCard({ card: c, menu, onMenu, onTag, onClaim, onAct, can = {} }) {
  if (c.sub === "Unclaimed") return <ClaimCard c={c} onClaim={onClaim} />;
  const tag = c.sub === "Tag Pending";
  const boxes = c.sub === "Approval Pending" && onAct ? <ApprovalBoxes c={c} can={can} onAct={onAct} /> : null;
  const next = onAct && !tag ? nextButton(c, can, onAct) : null;
  const park = parkingText(c);
  const day0 = num(c.stage1Day0At);
  const ext = day0 && c.stage1DeadlineAt && c.ring?.total ? Math.round((num(c.stage1DeadlineAt) - day0) / 86400000) - c.ring.total : 0;
  return (
    <div className={css.card}>
      <div className={css.cardH}>
        <div className={css.col}>
          <p className={css.u}>{unitLine(c)}</p>
          <div className={css.row}>
            <Chip text="Applied" tone="c-pur" dot={false} />
            {sdrDone(c) ? <Chip text="SDR Complete" tone="c-ok" /> : <Chip text="SDR Pending" tone="c-err" />}
            {/* M6-F11 (DC-123 / DC-158): paid earlier and the price changed */}
            {!sdrDone(c) && c.sdrMoney?.difference && stepDone(c, 1, "SDR_PAYMENT") === false && <Chip text={`SDR difference due ₹${Math.round(c.sdrMoney.difference.amount).toLocaleString("en-IN")}`} tone="c-err" />}
            {c.sdrMoney?.refund && <Chip text={c.sdrMoney.refund.status === "REFUNDED" ? "SDR refund done" : `SDR refund ₹${Math.round(c.sdrMoney.refund.amount).toLocaleString("en-IN")} in progress`} tone={c.sdrMoney.refund.status === "REFUNDED" ? "c-ok" : "c-pur"} />}
            {!tag && (c.isEmailSent ? <Chip text="Email Sent" tone="c-pur" /> : <Chip text="Email not sent" tone="c-err" />)}
            {!tag && !afbConfirmed(c) && <Chip text="Application for Booking not confirmed" tone="c-warn" />}
            {!tag && (kycDone(c) ? <Chip text="Agreement KYC Done" tone="c-ok" /> : <Chip text="Agreement KYC Pending" tone="c-warn" />)}
            {!tag && <OcrChip c={c} />}
            {!tag && (avChanged(c) ? <Chip text="AV Changed" tone="c-warn" /> : <Chip text="AV Not Changed" tone="c-lt" />)}
            {ext > 0 && <Chip text={`Extended +${ext} days`} tone="c-warn" />}
          </div>
        </div>
        <div className={css.row} style={{ gap: ".4rem", flexWrap: "nowrap" }}>
          <DayRing ring={c.ring} />
          <CardMenu items={menu} onPick={onMenu} />
        </div>
      </div>
      <div className={css.col} style={{ gap: ".6rem" }}>
        <div className={css.row} style={{ alignItems: "baseline", gap: ".5rem" }}>
          <p className={css.u}>{c.leadName}</p>
          <p className={css.lid}>{lid(c.leadDisplayId)}</p>
        </div>
        <div className={css.row} style={{ justifyContent: "space-between", gap: ".6rem" }}>
          <Chip text={leadLabel(c)} tone={leadLabel(c).startsWith("VJOP") ? "c-err" : "c-lt"} dot={false} />
          <div className={css.row} style={{ gap: ".6rem", justifyContent: "flex-end" }}>
            {c.isSanctioned === false && <Chip text="Waiting for Sanction" tone="c-warn" dot={false} />}
            <DiscBox c={c} />
          </div>
        </div>
      </div>
      <div className={css.meta}>
        <div className={css.r}>
          <span>VJ SM - {c.smName || "—"}</span>
          <span>VJ CRM - {c.crmName || "—"}</span>
        </div>
        <div className={css.r}>
          <span>{c.displayUnitType} (Display unit type)</span>
        </div>
        <div className={css.r}>
          <span>Applied On: {fmt(c.stage1Day0At)}</span>
        </div>
        {park && (
          <div className={css.r}>
            <span>Parking: {park}</span>
          </div>
        )}
        {c.isRejectChanges && c.rejectionReason && (
          <div className={css.r}>
            <span style={{ color: "#B42318" }}>
              <b>Rejection Reason :</b> {c.rejectionReason}
            </span>
          </div>
        )}
      </div>
      {!tag && (
        <Bars
          steps={tracker(c)}
          title="Application for Booking confirmed by OTP (the Agreement KYC opens after it) · KYC submitted · review draft confirmed by OTP · AV and payment plan approved · parking selected · SDR paid"
        />
      )}
      <div className={css.cardF} style={(c.discounts?.line && !tag) || boxes ? { flexDirection: "column", alignItems: "stretch" } : undefined}>
        {/* M4-F7: read-only loyalty line ("checked by the system; nothing for you to do") */}
        {c.discounts?.line && !tag && <CardMessage message={{ short: c.discounts.line.title, full: c.discounts.line.text || c.discounts.line.title, tone: c.discounts.line.tone }} />}
        {/* M5-F2 / F9: Approval needed boxes replace the one-line "Approval Pending" */}
        {boxes ||
          (next ? (
            <div className={css.row} style={{ justifyContent: "space-between", flexWrap: "nowrap", gap: "1rem", width: "100%" }}>
              <CardMessage message={c.message} />
              {next}
            </div>
          ) : (
            <CardMessage message={c.message} />
          ))}
        {tag && onTag && (
          <div className={css.row} style={{ gap: "1rem", flex: "none" }}>
            <button type="button" className={`${css.btn} ${css.btnDanger}`} onClick={() => onTag(c, "reject")}>Reject</button>
            <button type="button" className={`${css.btn} ${css.btnCard}`} onClick={() => onTag(c, "accept")}>Accept</button>
          </div>
        )}
      </div>
    </div>
  );
}
